import { useMemo, useState } from "react";
import {
  List,
  TextField,
  WrapperField,
  DatagridConfigurable,
  WithRecord,
  DateField,
  BooleanField,
  NumberField,
  BulkDeleteWithConfirmButton,
  FunctionField,
  useResourceContext,
  useTheme,
  RecordContextProvider,
  ChipField,
} from "react-admin";
import CustomerDebtPaymentAddDialog from "../../components/customerDebtPayments/create/CustomerDebtPaymentAddDialog";
import CustomerDebtPaymentEditDialog from "../../components/customerDebtPayments/edit/CustomerDebtPaymentEditDialog";
import customerDebtPaymentFilterList from "../../components/customerDebtPayments/filter/customerDebtPaymentFilterList";
import TableListActions from "../../components/tableListActions/TableListActions";
import CustomerDebtPaymentExpandList from "../../components/customerDebtPayments/expand/CustomerDebtPaymentExpandList";
import EditBtn from "../../components/editBtn/EditBtn";
import ConfirmBtn from "../../components/confirmBtn/ConfirmBtn";
import DoneIcon from "@mui/icons-material/Done";
import CloseIcon from "@mui/icons-material/Close";
import PrintHandler from "../../components/printHandler/PrintHandler";
import PrintBtn from "../../components/printBtn/PrintBtn";
import PrintContent from "../../components/customerDebtPayments/print/PrintContent";
import getFirstAndLastDateOfCurrentMonth from "../../utils/firstLastDate";
import MyDatagridBody from "../../components/common/MyDatagridBody";
import CustomerDebtPaymentFooterList from "../../components/customerDebtPayments/footer/CustomerDebtPaymentFooterList";
import useCompanyScopedListFilters from "../../hooks/useCompanyScopedListFilters";

const [firstDate, lastDate] = getFirstAndLastDateOfCurrentMonth();

const CustomerDebtPaymentList = () => {
  const resource = useResourceContext();
  const { permissions, canSeeCompanyColumn, filterDefaultValues } =
    useCompanyScopedListFilters({
      start_date: firstDate,
      end_date: lastDate,
    });
  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [recordId, setRecordId] = useState();
  const [resetPrintContent, setResetPrintContent] = useState();
  const [printRecord, setPrintRecord] = useState({}); // initial value wajib object kosong untuk cek key id di dependency useeffect DeliveryNote.js
  const [theme] = useTheme("light");

  const handleClickOpenAdd = () => {
    setOpenAdd(true);
  };
  const handleClickOpenEdit = (recId) => () => {
    setRecordId(recId);
    setOpenEdit(true);
  };
  const list = useMemo(
    () => (
      <List
        title="mine.list_title.customer_debt_payment"
        empty={false}
        actions={
          <TableListActions
            createHandler={handleClickOpenAdd}
            exporter={false}
            createPermission="create_customer_debt_payment"
          />
        }
        filters={customerDebtPaymentFilterList(permissions)}
        queryOptions={{ meta: { sort: ["code DESC"] } }} // overide default sort
        filterDefaultValues={filterDefaultValues}
      >
        <DatagridConfigurable
          body={<MyDatagridBody footer={<CustomerDebtPaymentFooterList />} />}
          // FIXME: semua datagrid perlu pake custom bulk action ini
          bulkActionButtons={<BulkDeleteWithConfirmButton />}
          expand={<CustomerDebtPaymentExpandList />}
          sx={{
            "& .RaDatagrid-expandedPanel > td": {
              p: 0,
              bgcolor: theme !== "dark" ? "#E7EBF0" : "grey.800",
            },
          }}
        >
          {canSeeCompanyColumn ? (
            <TextField source="company.alias" label="mine.label.depo" />
          ) : null}
          <TextField source="code" label="mine.label.receipt_no" />
          <DateField
            source="date"
            label="mine.label.date"
            options={{
              year: "numeric",
              month: "short",
              day: "2-digit",
            }}
          />
          <TextField source="customer.name" label="mine.label.customer" />
          <TextField source="tbp" />
          <FunctionField
            textAlign="center"
            label="mine.label.type"
            render={(record) => {
              let rec = {};
              switch (record.type) {
                case 1:
                  rec.type = "Transfer";
                  rec.color = "success";
                  break;
                case 2:
                  rec.type = "Cash";
                  rec.color = "primary";
                  break;
                case 3:
                  rec.type = "Giro";
                  rec.color = "warning";
                  break;
                default:
                  rec.type = "-";
                  rec.color = "default";
              }
              return (
                <RecordContextProvider value={rec}>
                  <ChipField source="type" color={rec.color} size="small" />
                </RecordContextProvider>
              );
            }}
          />
          <NumberField source="total" />
          <TextField source="note" label="mine.label.note" />
          <BooleanField
            source="approved"
            label="mine.label.approved"
            textAlign="center"
          />
          <WrapperField label="mine.label.actions" textAlign="center">
            <WithRecord
              render={(record) => {
                return !record.approved ? (
                  <>
                    <EditBtn
                      editHandler={handleClickOpenEdit(record.id)}
                      sx={{ mr: 1 }}
                    />
                    <ConfirmBtn
                      title="mine.message.approve"
                      content="mine.message.approve_content"
                      label="mine.btn.approve"
                      resource={resource + "/approve"}
                      startIcon={<DoneIcon />}
                    />
                  </>
                ) : (
                  <>
                    {permissions && permissions.administrator && (
                      <ConfirmBtn
                        title="mine.message.unapprove"
                        content="mine.message.unapprove_content"
                        label="mine.label.unapprove"
                        resource={resource + "/unapprove"}
                        startIcon={<CloseIcon />}
                        color="error"
                      />
                    )}
                    <PrintBtn
                      dialogProps={{ title: "mine.dialog.print" }}
                      recordId={record.id}
                      setPrintRecord={setPrintRecord}
                      setResetPrintContent={setResetPrintContent}
                    />
                  </>
                );
              }}
            />
          </WrapperField>
        </DatagridConfigurable>
      </List>
    ),
    [theme, permissions, canSeeCompanyColumn, filterDefaultValues]
  );
  return (
    <>
      {list}
      <CustomerDebtPaymentAddDialog open={openAdd} setOpen={setOpenAdd} />
      <CustomerDebtPaymentEditDialog
        open={openEdit}
        setOpen={setOpenEdit}
        recordId={recordId}
      />
      <PrintHandler
        record={printRecord}
        resetPrintContent={resetPrintContent}
        Content={PrintContent} //{CustomerDebtPaymentPrint}
      />
    </>
  );
};

export default CustomerDebtPaymentList;
